// src/services/apiService.js
// Frontend API Client for Chapter 24 PHP Backend with Cryptographic Authorization Headers

const API_BASE = '/api';

/**
 * Retrieve raw JWT authentication token from session storage
 */
export function getAuthToken() {
  try {
    const raw = localStorage.getItem('ch24_auth_session');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.token) {
        return parsed.token;
      }
    }
  } catch {
    // Ignore JSON parse errors
  }
  return '';
}

/**
 * Attach token to document URL for seamless image and PDF previews
 */
export function getAuthenticatedDocUrl(url) {
  if (!url) return '';
  const token = getAuthToken();
  if (!token || !url.includes('/document.php')) return url;
  if (url.includes('token=')) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
}

/**
 * Retrieve authorization headers using active token in session storage
 */
export function getAuthHeaders() {
  const token = getAuthToken();
  if (token) {
    return {
      'Authorization': `Bearer ${token}`
    };
  }
  return {};
}

export const apiService = {
  // 1. Applications & 6-Point KYC
  async getApplications(status = 'ALL', id = null) {
    if (!getAuthToken()) {
      return { success: false, error: 'Authentication required', unauthorized: true, data: [] };
    }
    try {
      const query = new URLSearchParams();
      if (status && status !== 'ALL') query.append('status', status);
      if (id) query.append('id', id);

      const queryString = query.toString() ? `?${query.toString()}` : '';
      const res = await fetch(`${API_BASE}/applications.php${queryString}`, {
        headers: getAuthHeaders()
      });
      if (res.status === 401) {
        return { success: false, error: 'Unauthorized', unauthorized: true, data: [] };
      }
      return await res.json();
    } catch (e) {
      console.warn('API fallback to local state:', e);
      return { success: false, error: e.message };
    }
  },

  async submitApplication(formData) {
    try {
      const res = await fetch(`${API_BASE}/applications.php`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async updateKyc(id, action, payload = {}) {
    try {
      const res = await fetch(`${API_BASE}/applications.php`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, action, ...payload })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 2. Room & Bed Allocation Matrix
  async getBeds() {
    try {
      const res = await fetch(`${API_BASE}/beds.php`, {
        headers: getAuthHeaders()
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async allocateBed(payload) {
    try {
      const res = await fetch(`${API_BASE}/beds.php`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 3. Active Residents
  async getResidents(id = null) {
    if (!getAuthToken()) {
      return { success: false, error: 'Authentication required', unauthorized: true, data: [] };
    }
    try {
      const url = id ? `${API_BASE}/residents.php?id=${encodeURIComponent(id)}` : `${API_BASE}/residents.php`;
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (res.status === 401) {
        return { success: false, error: 'Unauthorized', unauthorized: true, data: [] };
      }
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 4. Billing & Invoices
  async getInvoices(residentId = null) {
    if (!getAuthToken()) {
      return { success: false, error: 'Authentication required', unauthorized: true, data: [] };
    }
    try {
      const url = residentId ? `${API_BASE}/invoices.php?resident_id=${encodeURIComponent(residentId)}` : `${API_BASE}/invoices.php`;
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (res.status === 401) {
        return { success: false, error: 'Unauthorized', unauthorized: true, data: [] };
      }
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async generateBatchInvoices(month) {
    try {
      const res = await fetch(`${API_BASE}/invoices.php`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type: 'batch_monthly', month })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async createCustomInvoice(payload) {
    try {
      const res = await fetch(`${API_BASE}/invoices.php`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type: 'custom', ...payload })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 5. Payments & Receipts
  async getReceipts(residentId = null) {
    if (!getAuthToken()) {
      return { success: false, error: 'Authentication required', unauthorized: true, data: [] };
    }
    try {
      const url = residentId ? `${API_BASE}/payments.php?resident_id=${encodeURIComponent(residentId)}` : `${API_BASE}/payments.php`;
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (res.status === 401) {
        return { success: false, error: 'Unauthorized', unauthorized: true, data: [] };
      }
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async recordPayment(payload) {
    try {
      const res = await fetch(`${API_BASE}/payments.php`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 6. Complaints
  async getComplaints(residentId = null) {
    if (!getAuthToken()) {
      return { success: false, error: 'Authentication required', unauthorized: true, data: [] };
    }
    try {
      const url = residentId ? `${API_BASE}/complaints.php?resident_id=${encodeURIComponent(residentId)}` : `${API_BASE}/complaints.php`;
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (res.status === 401) {
        return { success: false, error: 'Unauthorized', unauthorized: true, data: [] };
      }
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async submitComplaint(payload) {
    try {
      const res = await fetch(`${API_BASE}/complaints.php`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async updateComplaint(id, payload) {
    try {
      const res = await fetch(`${API_BASE}/complaints.php`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, ...payload })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 7. Settlements
  async getSettlements(residentId = null) {
    if (!getAuthToken()) {
      return { success: false, error: 'Authentication required', unauthorized: true, data: [] };
    }
    try {
      const url = residentId ? `${API_BASE}/settlements.php?resident_id=${encodeURIComponent(residentId)}` : `${API_BASE}/settlements.php`;
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (res.status === 401) {
        return { success: false, error: 'Unauthorized', unauthorized: true, data: [] };
      }
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async submitNotice(payload) {
    try {
      const res = await fetch(`${API_BASE}/settlements.php`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async updateSettlement(id, action, payload = {}) {
    try {
      const res = await fetch(`${API_BASE}/settlements.php`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, action, ...payload })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 8. File Upload (Aadhaar / PAN / Photo)
  async uploadFile(file, docType = 'doc') {
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('docType', docType);
      const res = await fetch(`${API_BASE}/upload.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 9. Auth & Status
  async login(payload) {
    try {
      const res = await fetch(`${API_BASE}/auth.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async trackStatus(identifier) {
    try {
      const res = await fetch(`${API_BASE}/auth.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'track_status', identifier })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 10. Leads & Inquiries CRM
  async submitLead(payload) {
    try {
      const res = await fetch(`${API_BASE}/leads.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async getLeads(status = 'ALL', search = '') {
    if (!getAuthToken()) {
      return { success: false, error: 'Authentication required', unauthorized: true, data: [] };
    }
    try {
      const query = new URLSearchParams();
      if (status !== 'ALL') query.append('status', status);
      if (search) query.append('search', search);

      const res = await fetch(`${API_BASE}/leads.php?${query.toString()}`, {
        headers: getAuthHeaders()
      });
      if (res.status === 401) {
        return { success: false, error: 'Unauthorized', unauthorized: true, data: [] };
      }
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async updateLead(id, status, notes = '') {
    try {
      const res = await fetch(`${API_BASE}/leads.php`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, status, notes })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async deleteLead(id) {
    try {
      const res = await fetch(`${API_BASE}/leads.php?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  // 11. System Notifications & Announcements
  async getNotifications() {
    try {
      const res = await fetch(`${API_BASE}/notifications.php`, {
        headers: getAuthHeaders()
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async addNotification(payload) {
    try {
      const res = await fetch(`${API_BASE}/notifications.php`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async deleteNotification(id) {
    try {
      const res = await fetch(`${API_BASE}/notifications.php?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async clearAllNotifications() {
    try {
      const res = await fetch(`${API_BASE}/notifications.php?action=clear_all`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },


  // 12. Email OTP Verification Service
  async sendEmailOtp(email) {
    try {
      const res = await fetch(`${API_BASE}/otp.php?action=send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async verifyEmailOtp(email, otp) {
    try {
      const res = await fetch(`${API_BASE}/otp.php?action=verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  async checkEmailOtp(email) {
    try {
      const res = await fetch(`${API_BASE}/otp.php?action=check&email=${encodeURIComponent(email)}`, {
        headers: getAuthHeaders()
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  }
};

export default apiService;
